/**
 * BackfillSearchIndexPage
 *
 * Admin-only utility that invokes the `backfillSearchIndex` callable. Walks
 * every CaseFile's completed documents, computes `searchableText` +
 * `aclUserIds` if missing, writes them back to Firestore, and publishes a
 * Pub/Sub event so the indexer upserts the doc into Discovery Engine.
 *
 * See: functions/src/search/backfillSearchIndex.ts and
 *      docs/architecture/vertex-ai-search-design.md §2.3.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  Play,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';
import { useAdminFunction } from '../hooks/useAdminApi';

interface BackfillRequest {
  force?: boolean;
  maxDocs?: number;
  reset?: boolean;
}

interface BackfillResponse {
  success: boolean;
  scanned: number;
  updated: number;
  skipped: number;
  failed: number;
  resumeCursor?: { userId: string; caseFileId: string } | null;
  done: boolean;
}

interface AggregateStats {
  scanned: number;
  updated: number;
  skipped: number;
  failed: number;
  runs: number;
}

const ZERO_STATS: AggregateStats = {
  scanned: 0,
  updated: 0,
  skipped: 0,
  failed: 0,
  runs: 0,
};

export function BackfillSearchIndexPage(): JSX.Element {
  const navigate = useNavigate();

  const { execute, loading, error } = useAdminFunction<
    BackfillRequest,
    BackfillResponse
  >('backfillSearchIndex');

  const [lastResponse, setLastResponse] = useState<BackfillResponse | null>(null);
  const [aggregate, setAggregate] = useState<AggregateStats>(ZERO_STATS);
  const [force, setForce] = useState(false);
  const [running, setRunning] = useState(false);

  async function runOnePage(opts: BackfillRequest = {}): Promise<BackfillResponse> {
    const response = await execute(opts);
    setLastResponse(response);
    setAggregate((prev) => ({
      scanned: prev.scanned + response.scanned,
      updated: prev.updated + response.updated,
      skipped: prev.skipped + response.skipped,
      failed: prev.failed + response.failed,
      runs: prev.runs + 1,
    }));
    return response;
  }

  /** Run pages until done === true or we hit an error. */
  async function runUntilDone(opts: BackfillRequest = {}): Promise<void> {
    setRunning(true);
    try {
      let response = await runOnePage({ ...opts, force });
      while (!response.done) {
        // Backend's own rate-limiting and checkpoint keeps each page cheap;
        // we just keep firing until it reports done.
        response = await runOnePage({ force });
      }
    } finally {
      setRunning(false);
    }
  }

  async function handleReset(): Promise<void> {
    setAggregate(ZERO_STATS);
    setLastResponse(null);
    await runUntilDone({ reset: true, force });
  }

  return (
    <div className="min-h-screen bg-airy">
      <header className="bg-white border-b border-soft-neutral px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-soft-neutral rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-muted" />
          </button>
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-evergreen" />
            <h1 className="text-xl font-semibold text-ink">
              Backfill Search Index
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        <section className="card p-6">
          <h2 className="text-lg font-semibold text-ink mb-2">What this does</h2>
          <p className="text-sm text-muted leading-relaxed mb-3">
            Walks every CaseFile's completed documents, populates{' '}
            <code className="text-xs bg-soft-neutral px-1 py-0.5 rounded">
              searchableText
            </code>{' '}
            and{' '}
            <code className="text-xs bg-soft-neutral px-1 py-0.5 rounded">
              aclUserIds
            </code>{' '}
            if missing, and publishes a search-index event so the document is
            (re-)upserted into Discovery Engine. Idempotent — already-indexed
            docs are skipped unless <em>Force reindex</em> is checked.
          </p>
          <p className="text-xs text-muted">
            Each page processes up to 5,000 documents and writes a checkpoint;
            this UI runs pages back-to-back until the backend reports{' '}
            <code className="text-xs bg-soft-neutral px-1 py-0.5 rounded">
              done: true
            </code>
            .
          </p>
        </section>

        <section className="card p-6">
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              disabled={running}
              className="w-4 h-4"
            />
            <span className="text-sm text-ink">
              Force reindex (process docs even if already indexed)
            </span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={() => runUntilDone()}
              disabled={running || loading}
              className="btn-primary flex items-center gap-2"
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Run backfill
                </>
              )}
            </button>

            <button
              onClick={handleReset}
              disabled={running || loading}
              className="btn-secondary flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset checkpoint &amp; restart
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </section>

        {(aggregate.runs > 0 || lastResponse) && (
          <section className="card p-6">
            <div className="flex items-center gap-2 mb-4">
              {lastResponse?.done ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-ink">Complete</h2>
                </>
              ) : running ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-muted" />
                  <h2 className="text-lg font-semibold text-ink">In progress</h2>
                </>
              ) : (
                <h2 className="text-lg font-semibold text-ink">Paused</h2>
              )}
            </div>

            <dl className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <Stat label="Pages" value={aggregate.runs} />
              <Stat label="Scanned" value={aggregate.scanned} />
              <Stat label="Updated" value={aggregate.updated} accent="green" />
              <Stat label="Skipped" value={aggregate.skipped} />
              <Stat
                label="Failed"
                value={aggregate.failed}
                accent={aggregate.failed > 0 ? 'red' : undefined}
              />
            </dl>

            {lastResponse?.resumeCursor && !lastResponse.done && (
              <p className="mt-4 text-xs text-muted">
                Last cursor: <code>{lastResponse.resumeCursor.userId}</code> /{' '}
                <code>{lastResponse.resumeCursor.caseFileId}</code>
              </p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'green' | 'red';
}): JSX.Element {
  const tone =
    accent === 'green'
      ? 'text-green-700'
      : accent === 'red'
      ? 'text-red-700'
      : 'text-ink';
  return (
    <div>
      <dt className="text-xs text-muted uppercase tracking-wide">{label}</dt>
      <dd className={`text-2xl font-semibold ${tone}`}>{value.toLocaleString()}</dd>
    </div>
  );
}
